import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaMale,
  FaFemale,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLock,
  FaChevronRight,
} from "react-icons/fa";
import "./DressCode.css";

const dressData = {
  men: {
    title: "Dress Code for Men",
    subtitle: "Traditional attire is required for Darshan",
    image: "./images/men.png",
    icon: <FaMale />,
    rules: [
      <>
        Traditional Kerala <strong>Mundu and Veshti</strong> (Upper Cloth)
        are mandatory for all men entering the temple.
      </>,
      <>
        Trousers, shorts and shirts must be left at the locker counter.
      </>,
    ],
  },

  women: {
    title: "Dress Code for Women",
    subtitle: "Traditional attire is required for Darshan",
    image: "./images/women.png",
    icon: <FaFemale />,
    rules: [
      <>
        Saree, Half-Saree and Pattu Pavadai with Blouse are permitted.
      </>,
      <>
        Modern clothing such as trousers or short dresses are not
        permitted.
      </>,
    ],
  },
};

const DressCode = () => {
  const [activeTab, setActiveTab] = useState("men");

  const current = dressData[activeTab];

  return (
    <section className="dress-code-section">

      <div className="dress-code-overlay"></div>

      <div className="dress-code-container">

        {/* Header */}
        <motion.div
          className="dress-code-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="dress-small-title">
            TEMPLE GUIDELINES
          </span>

          <div className="dress-title-row">
            <span></span>

            <h2>
              Dress Code
              <strong> Guidelines for Darshan</strong>
            </h2>

            <span></span>
          </div>

          <p>
            Devotees are requested to follow the traditional dress code
            while entering the temple premises.
          </p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          className="dress-modern-card"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >

          {/* Tabs */}
          <div className="dress-tabs">

            <button
              className={activeTab === "men" ? "active" : ""}
              onClick={() => setActiveTab("men")}
            >
              <span className="tab-icon">
                <FaMale />
              </span>

              <span>
                <small>TRADITIONAL ATTIRE</small>
                Men
              </span>

              {activeTab === "men" && (
                <motion.div
                  className="tab-active-line"
                  layoutId="activeTab"
                />
              )}
            </button>

            <button
              className={activeTab === "women" ? "active" : ""}
              onClick={() => setActiveTab("women")}
            >
              <span className="tab-icon">
                <FaFemale />
              </span>

              <span>
                <small>TRADITIONAL ATTIRE</small>
                Women
              </span>

              {activeTab === "women" && (
                <motion.div
                  className="tab-active-line"
                  layoutId="activeTab"
                />
              )}
            </button>

          </div>

          {/* Content */}
          <div className="dress-content">

            {/* Image */}
            <div className="dress-visual">

              <div className="visual-decoration decoration-one"></div>
              <div className="visual-decoration decoration-two"></div>

              <AnimatePresence mode="wait">

                <motion.img
                  key={activeTab}
                  src={current.image}
                  alt={current.title}
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    x: 30,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    x: -30,
                  }}
                  transition={{ duration: 0.45 }}
                />

              </AnimatePresence>

              <div className="image-label">
                <span>{current.icon}</span>
                <div>
                  <small>APPROVED ATTIRE</small>
                  <strong>
                    {activeTab === "men" ? "Men" : "Women"}
                  </strong>
                </div>
              </div>

            </div>

            {/* Details */}
            <div className="dress-details">

              <AnimatePresence mode="wait">

                <motion.div
                  key={activeTab}
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    y: -20,
                  }}
                  transition={{ duration: 0.4 }}
                >

                  <div className="detail-icon">
                    {current.icon}
                  </div>

                  <span className="detail-category">
                    DARSHAN DRESS CODE
                  </span>

                  <h3>
                    {current.title}
                  </h3>

                  <p className="detail-subtitle">
                    {current.subtitle}
                  </p>

                  <div className="dress-rule-list">

                    {current.rules.map((rule, index) => (
                      <div
                        className={`dress-rule ${
                          index === 1 ? "warning-rule" : ""
                        }`}
                        key={index}
                      >

                        <span className="rule-icon">
                          {index === 1 ? (
                            <FaExclamationTriangle />
                          ) : (
                            <FaCheckCircle />
                          )}
                        </span>

                        <p>{rule}</p>

                      </div>
                    ))}

                  </div>

                  <div className="locker-info">
                    <FaLock />

                    <div>
                      <strong>Locker Facility</strong>

                      <p>
                        Restricted clothing can be safely deposited
                        at the locker counter.
                      </p>
                    </div>

                    <FaChevronRight className="locker-arrow" />
                  </div>

                </motion.div>

              </AnimatePresence>

            </div>

          </div>

        </motion.div>

        {/* Important Notice */}
        <motion.div
          className="modern-notice"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          <div className="notice-symbol">
            <FaExclamationTriangle />
          </div>

          <div className="notice-text">

            <span>IMPORTANT NOTICE</span>

            <h3>
              Dress code is strictly mandatory
            </h3>

            <p>
              The prescribed dress code applies to all pilgrims for
              daily Darshan and special Sevas. We strictly adhere to
              ancient Kerala Temple traditions.
            </p>

          </div>

          <div className="notice-badge">
            <FaCheckCircle />
            <span>Mandatory</span>
          </div>

        </motion.div>

      </div>
    </section>
  );
};

export default DressCode;